import { HP_CODES } from '@nxsys/constants/AppConst';
import { IRELAND } from '@nxsys/store/constants/keys';
import { VATCode } from '@nxsys/store/slices/paymentSetting/types';
import { Option, PayrollCountryType } from '@nxsys/types/models/Common';
import { map, split, toString } from 'lodash';
import postalCodes from 'postal-codes-js';
import { getAppConfig } from './appConfigs';
import { YesNoNAEnum } from '@nxsys/constants/AppEnums';
import numeral from 'numeral';
import moment from 'moment';

export const formatStatus = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'BLOCKED':
      return 'Blocked';
    case 'ON_HOLD':
      return 'On hold';
    case 'LOST':
      return 'Lost';
    case 'PENDING':
      return 'Pending';
    case 'INACTIVE':
      return 'Inactive';
    case 'PENDING_IMPORT':
      return 'Pending Import';
    case 'SUCCESSFULLY_IMPORTED':
      return 'Successfully Imported';
    case 'CANCELED':
      return 'Canceled';
    case 'HIGH':
      return 'High';
    case 'MEDIUM':
      return 'Medium';
    case 'LOW':
      return 'Low';
    case 'APPROVED':
      return 'Approved';
    case 'REJECT':
      return 'Reject';
    case 'REJECTED':
      return 'Rejected';
    case 'FAILED':
      return 'Failed';
    case 'ACCEPT':
      return 'Accept';
    case 'SUCCESS':
      return 'Success';
    case 'SUBMITTED':
      return 'Submitted';
    case 'PART_REGISTERED':
      return 'Part Registered';
    case 'REGISTRATION_COMPLETE':
      return 'Registration Complete';
    case 'VERIFIED':
      return 'Verified';
    case 'INVALID':
      return 'Invalid';
    case 'TRUE':
      return 'Yes';
    case 'FALSE':
      return 'No';
    case 'NOT_AVAILABLE':
      return 'N/A';
    case 'WARNING':
      return 'Warning';
    case 'UPDATED':
      return 'Updated';
    case 'NEW':
      return 'New';
    case 'FINISHED':
      return 'Finished';
    case 'STOPPED':
      return 'Stopped';
    case 'ERROR':
      return 'Error';
    case 'CANCEL':
      return 'Cancel';
    case 'OVERDUE':
      return 'Overdue';
    case 'CLOSED':
      return 'Closed';
    case 'READY':
      return 'Ready';
    case 'PROCESSING':
      return 'Processing';
    case 'PENDING_CONFIRMATION':
      return 'Pending Confirmation';
    case 'CONFIRMED':
      return 'Confirmed';
    case 'APPLIED':
      return 'Applied';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NOT_SENT':
      return 'Not Sent';
    default:
      return status;
  }
};

export const getFileNameFromUrl = (url: string) => {
  return url.split('/').pop()?.split('?')[0] || 'file';
};

export const getFileTypeFromUrl = (url: string) => {
  const ext = url.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'svg':
    case 'webp':
      return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    case 'pdf':
      return 'application/pdf';
    case 'txt':
      return 'text/plain';
    case 'csv':
      return 'text/csv';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'zip':
      return 'application/zip';
    case 'rar':
      return 'application/x-rar-compressed';
    default: {
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.includes('/jpg/')) return 'image/jpeg';
      if (lowerUrl.includes('/jpeg/')) return 'image/jpeg';
      if (lowerUrl.includes('/png/')) return 'image/png';
      if (lowerUrl.includes('/gif/')) return 'image/gif';
      if (lowerUrl.includes('/svg/')) return 'image/svg+xml';
      if (lowerUrl.includes('/webp/')) return 'image/webp';
      if (lowerUrl.includes('/pdf/')) return 'application/pdf';
      if (lowerUrl.includes('/txt/')) return 'text/plain';
      if (lowerUrl.includes('/csv/')) return 'text/csv';
      if (lowerUrl.includes('/docx/'))
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (lowerUrl.includes('/doc/')) return 'application/msword';
      if (lowerUrl.includes('/xlsx/'))
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (lowerUrl.includes('/xls/')) return 'application/vnd.ms-excel';
      if (lowerUrl.includes('/pptx/'))
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      if (lowerUrl.includes('/ppt/')) return 'application/vnd.ms-powerpoint';
      if (lowerUrl.includes('/zip/')) return 'application/zip';
      if (lowerUrl.includes('/rar/')) return 'application/x-rar-compressed';
      return 'application/octet-stream';
    }
  }
};

export { getTaxWeekTaxYear, isNIDirectorMidYear } from './taxYear';

export { formatFullName } from './name';

export {
  appConfigManager,
  getAppConfigs,
  getAppConfig,
  isAppConfigsInitialized,
} from './appConfigs';

export const convertCamelToSpaces = (str: string) => {
  if (!str) return '';

  // First, find all sequences of uppercase letters
  const uppercaseSequences = str.match(/[A-Z]{2,}/g) || [];

  // Replace each sequence with a marked version
  let result = str;
  uppercaseSequences.forEach((seq) => {
    result = result.replace(seq, `__${seq}__`);
  });

  // Add spaces between camelCase transitions
  result = result
    .replace(/([A-Z])(?=[a-z])/g, ' $1') // Space before single capital followed by lowercase
    .replace(/([a-z])([A-Z])/g, '$1 $2'); // Space between lowercase and capital

  // Restore the uppercase sequences
  uppercaseSequences.forEach((seq) => {
    result = result.replace(`__${seq}__`, seq);
  });

  // Capitalize first letter and trim
  return result.replace(/^./, (str) => str.toUpperCase()).trim();
};

export const exportFileCSVFromValues = (data: string, fileName: string) => {
  const universalBOM = '\uFEFF';
  const element = document.createElement('a');
  element.setAttribute(
    'href',
    `data:application/csv;charset=utf-8, ${encodeURIComponent(
      universalBOM + data,
    )}`,
  );
  element.setAttribute('download', `${fileName}.csv`);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

export const getCurrencySymbolByCountry = (country: PayrollCountryType) => {
  switch (country) {
    case 'IRELAND':
      return '€';
    default:
      return '£';
  }
};

export const FILE_TYPE_VALUE = {
  SUBCONTRACTOR: 'SUBCONTRACTOR',
  PAYROLL: 'PAYROLL',
  SELF_BILL: 'SELF_BILL',
  AGENCY: 'AGENCY',
  ALL: 'ALL',
};

// export const ROLES = {
//   SUPPER_ADMIN: 'SUPPER_ADMIN',
//   ADMIN: 'ADMIN',
//   EMPLOYER: 'EMPLOYER',
//   EMPLOYEE: 'EMPLOYEE',
// };

export const showCurrencyByCountry = (
  country: PayrollCountryType = 'UNITED_KINGDOM',
) => {
  if (country === IRELAND) {
    return '€';
  }
  return '£';
};

export const countryList = [
  { label: 'Afghanistan', value: 'AF' },
  { label: 'Åland Islands', value: 'AX' },
  { label: 'Albania', value: 'AL' },
  { label: 'Algeria', value: 'DZ' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'Andorra', value: 'AD' },
  { label: 'Angola', value: 'AO' },
  { label: 'Anguilla', value: 'AI' },
  { label: 'Antarctica', value: 'AQ' },
  { label: 'Antigua and Barbuda', value: 'AG' },
  { label: 'Argentina', value: 'AR' },
  { label: 'Armenia', value: 'AM' },
  { label: 'Aruba', value: 'AW' },
  { label: 'Australia', value: 'AU' },
  { label: 'Austria', value: 'AT' },
  { label: 'Azerbaijan', value: 'AZ' },
  { label: 'Bahamas', value: 'BS' },
  { label: 'Bahrain', value: 'BH' },
  { label: 'Bangladesh', value: 'BD' },
  { label: 'Barbados', value: 'BB' },
  { label: 'Belarus', value: 'BY' },
  { label: 'Belgium', value: 'BE' },
  { label: 'Belize', value: 'BZ' },
  { label: 'Benin', value: 'BJ' },
  { label: 'Bermuda', value: 'BM' },
  { label: 'Bhutan', value: 'BT' },
  { label: 'Bolivia', value: 'BO' },
  { label: 'Bosnia and Herzegovina', value: 'BA' },
  { label: 'Botswana', value: 'BW' },
  { label: 'Bouvet Island', value: 'BV' },
  { label: 'Brazil', value: 'BR' },
  { label: 'British Indian Ocean Territory', value: 'IO' },
  { label: 'Brunei Darussalam', value: 'BN' },
  { label: 'Bulgaria', value: 'BG' },
  { label: 'Burkina Faso', value: 'BF' },
  { label: 'Burundi', value: 'BI' },
  { label: 'Cambodia', value: 'KH' },
  { label: 'Cameroon', value: 'CM' },
  { label: 'Canada', value: 'CA' },
  { label: 'Cape Verde', value: 'CV' },
  { label: 'Cayman Islands', value: 'KY' },
  { label: 'Central African Republic', value: 'CF' },
  { label: 'Chad', value: 'TD' },
  { label: 'Chile', value: 'CL' },
  { label: 'China', value: 'CN' },
  { label: 'Christmas Island', value: 'CX' },
  { label: 'Cocos (Keeling) Islands', value: 'CC' },
  { label: 'Colombia', value: 'CO' },
  { label: 'Comoros', value: 'KM' },
  { label: 'Congo', value: 'CG' },
  { label: 'Congo, The Democratic Republic of the', value: 'CD' },
  { label: 'Cook Islands', value: 'CK' },
  { label: 'Costa Rica', value: 'CR' },
  { label: "Cote D'Ivoire", value: 'CI' },
  { label: 'Croatia', value: 'HR' },
  { label: 'Cuba', value: 'CU' },
  { label: 'Cyprus', value: 'CY' },
  { label: 'Czech Republic', value: 'CZ' },
  { label: 'Denmark', value: 'DK' },
  { label: 'Djibouti', value: 'DJ' },
  { label: 'Dominica', value: 'DM' },
  { label: 'Dominican Republic', value: 'DO' },
  { label: 'Ecuador', value: 'EC' },
  { label: 'Egypt', value: 'EG' },
  { label: 'El Salvador', value: 'SV' },
  { label: 'Equatorial Guinea', value: 'GQ' },
  { label: 'Eritrea', value: 'ER' },
  { label: 'Estonia', value: 'EE' },
  { label: 'Ethiopia', value: 'ET' },
  { label: 'Falkland Islands (Malvinas)', value: 'FK' },
  { label: 'Faroe Islands', value: 'FO' },
  { label: 'Fiji', value: 'FJ' },
  { label: 'Finland', value: 'FI' },
  { label: 'France', value: 'FR' },
  { label: 'French Guiana', value: 'GF' },
  { label: 'French Polynesia', value: 'PF' },
  { label: 'French Southern Territories', value: 'TF' },
  { label: 'Gabon', value: 'GA' },
  { label: 'Gambia', value: 'GM' },
  { label: 'Georgia', value: 'GE' },
  { label: 'Germany', value: 'DE' },
  { label: 'Ghana', value: 'GH' },
  { label: 'Gibraltar', value: 'GI' },
  { label: 'Greece', value: 'GR' },
  { label: 'Greenland', value: 'GL' },
  { label: 'Grenada', value: 'GD' },
  { label: 'Guadeloupe', value: 'GP' },
  { label: 'Guam', value: 'GU' },
  { label: 'Guatemala', value: 'GT' },
  { label: 'Guernsey', value: 'GG' },
  { label: 'Guinea', value: 'GN' },
  { label: 'Guinea-Bissau', value: 'GW' },
  { label: 'Guyana', value: 'GY' },
  { label: 'Haiti', value: 'HT' },
  { label: 'Heard Island and Mcdonald Islands', value: 'HM' },
  { label: 'Holy See (Vatican City State)', value: 'VA' },
  { label: 'Honduras', value: 'HN' },
  { label: 'Hong Kong', value: 'HK' },
  { label: 'Hungary', value: 'HU' },
  { label: 'Iceland', value: 'IS' },
  { label: 'India', value: 'IN' },
  { label: 'Indonesia', value: 'ID' },
  { label: 'Iran, Islamic Republic Of', value: 'IR' },
  { label: 'Iraq', value: 'IQ' },
  { label: 'Ireland', value: 'IE' },
  { label: 'Isle of Man', value: 'IM' },
  { label: 'Israel', value: 'IL' },
  { label: 'Italy', value: 'IT' },
  { label: 'Jamaica', value: 'JM' },
  { label: 'Japan', value: 'JP' },
  { label: 'Jersey', value: 'JE' },
  { label: 'Jordan', value: 'JO' },
  { label: 'Kazakhstan', value: 'KZ' },
  { label: 'Kenya', value: 'KE' },
  { label: 'Kiribati', value: 'KI' },
  { label: "Korea, Democratic People'S Republic of", value: 'KP' },
  { label: 'Korea, Republic of', value: 'KR' },
  { label: 'Kuwait', value: 'KW' },
  { label: 'Kyrgyzstan', value: 'KG' },
  { label: "Lao People'S Democratic Republic", value: 'LA' },
  { label: 'Latvia', value: 'LV' },
  { label: 'Lebanon', value: 'LB' },
  { label: 'Lesotho', value: 'LS' },
  { label: 'Liberia', value: 'LR' },
  { label: 'Libyan Arab Jamahiriya', value: 'LY' },
  { label: 'Liechtenstein', value: 'LI' },
  { label: 'Lithuania', value: 'LT' },
  { label: 'Luxembourg', value: 'LU' },
  { label: 'Macao', value: 'MO' },
  { label: 'Macedonia, The Former Yugoslav Republic of', value: 'MK' },
  { label: 'Madagascar', value: 'MG' },
  { label: 'Malawi', value: 'MW' },
  { label: 'Malaysia', value: 'MY' },
  { label: 'Maldives', value: 'MV' },
  { label: 'Mali', value: 'ML' },
  { label: 'Malta', value: 'MT' },
  { label: 'Marshall Islands', value: 'MH' },
  { label: 'Martinique', value: 'MQ' },
  { label: 'Mauritania', value: 'MR' },
  { label: 'Mauritius', value: 'MU' },
  { label: 'Mayotte', value: 'YT' },
  { label: 'Mexico', value: 'MX' },
  { label: 'Micronesia, Federated States of', value: 'FM' },
  { label: 'Moldova, Republic of', value: 'MD' },
  { label: 'Monaco', value: 'MC' },
  { label: 'Mongolia', value: 'MN' },
  { label: 'Montserrat', value: 'MS' },
  { label: 'Morocco', value: 'MA' },
  { label: 'Mozambique', value: 'MZ' },
  { label: 'Myanmar', value: 'MM' },
  { label: 'Namibia', value: 'NA' },
  { label: 'Nauru', value: 'NR' },
  { label: 'Nepal', value: 'NP' },
  { label: 'Netherlands', value: 'NL' },
  { label: 'Netherlands Antilles', value: 'AN' },
  { label: 'New Caledonia', value: 'NC' },
  { label: 'New Zealand', value: 'NZ' },
  { label: 'Nicaragua', value: 'NI' },
  { label: 'Niger', value: 'NE' },
  { label: 'Nigeria', value: 'NG' },
  { label: 'Niue', value: 'NU' },
  { label: 'Norfolk Island', value: 'NF' },
  { label: 'Northern Mariana Islands', value: 'MP' },
  { label: 'Norway', value: 'NO' },
  { label: 'Oman', value: 'OM' },
  { label: 'Pakistan', value: 'PK' },
  { label: 'Palau', value: 'PW' },
  { label: 'Palestinian Territory, Occupied', value: 'PS' },
  { label: 'Panama', value: 'PA' },
  { label: 'Papua New Guinea', value: 'PG' },
  { label: 'Paraguay', value: 'PY' },
  { label: 'Peru', value: 'PE' },
  { label: 'Philippines', value: 'PH' },
  { label: 'Pitcairn', value: 'PN' },
  { label: 'Poland', value: 'PL' },
  { label: 'Portugal', value: 'PT' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Qatar', value: 'QA' },
  { label: 'Reunion', value: 'RE' },
  { label: 'Romania', value: 'RO' },
  { label: 'Russian Federation', value: 'RU' },
  { label: 'RWANDA', value: 'RW' },
  { label: 'Saint Helena', value: 'SH' },
  { label: 'Saint Kitts and Nevis', value: 'KN' },
  { label: 'Saint Lucia', value: 'LC' },
  { label: 'Saint Pierre and Miquelon', value: 'PM' },
  { label: 'Saint Vincent and the Grenadines', value: 'VC' },
  { label: 'Samoa', value: 'WS' },
  { label: 'San Marino', value: 'SM' },
  { label: 'Sao Tome and Principe', value: 'ST' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'Senegal', value: 'SN' },
  { label: 'Serbia and Montenegro', value: 'CS' },
  { label: 'Seychelles', value: 'SC' },
  { label: 'Sierra Leone', value: 'SL' },
  { label: 'Singapore', value: 'SG' },
  { label: 'Slovakia', value: 'SK' },
  { label: 'Slovenia', value: 'SI' },
  { label: 'Solomon Islands', value: 'SB' },
  { label: 'Somalia', value: 'SO' },
  { label: 'South Africa', value: 'ZA' },
  { label: 'South Georgia and the South Sandwich Islands', value: 'GS' },
  { label: 'Spain', value: 'ES' },
  { label: 'Sri Lanka', value: 'LK' },
  { label: 'Sudan', value: 'SD' },
  { label: 'Suriname', value: 'SR' },
  { label: 'Svalbard and Jan Mayen', value: 'SJ' },
  { label: 'Swaziland', value: 'SZ' },
  { label: 'Sweden', value: 'SE' },
  { label: 'Switzerland', value: 'CH' },
  { label: 'Syrian Arab Republic', value: 'SY' },
  { label: 'Taiwan, Province of China', value: 'TW' },
  { label: 'Tajikistan', value: 'TJ' },
  { label: 'Tanzania, United Republic of', value: 'TZ' },
  { label: 'Thailand', value: 'TH' },
  { label: 'Timor-Leste', value: 'TL' },
  { label: 'Togo', value: 'TG' },
  { label: 'Tokelau', value: 'TK' },
  { label: 'Tonga', value: 'TO' },
  { label: 'Trinidad and Tobago', value: 'TT' },
  { label: 'Tunisia', value: 'TN' },
  { label: 'Turkey', value: 'TR' },
  { label: 'Turkmenistan', value: 'TM' },
  { label: 'Turks and Caicos Islands', value: 'TC' },
  { label: 'Tuvalu', value: 'TV' },
  { label: 'Uganda', value: 'UG' },
  { label: 'Ukraine', value: 'UA' },
  { label: 'United Arab Emirates', value: 'AE' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'United States', value: 'US' },
  { label: 'United States Minor Outlying Islands', value: 'UM' },
  { label: 'Uruguay', value: 'UY' },
  { label: 'Uzbekistan', value: 'UZ' },
  { label: 'Vanuatu', value: 'VU' },
  { label: 'Venezuela', value: 'VE' },
  { label: 'Viet Nam', value: 'VN' },
  { label: 'Virgin Islands, British', value: 'VG' },
  { label: 'Virgin Islands, U.S.', value: 'VI' },
  { label: 'Wallis and Futuna', value: 'WF' },
  { label: 'Western Sahara', value: 'EH' },
  { label: 'Yemen', value: 'YE' },
  { label: 'Zambia', value: 'ZM' },
  { label: 'Zimbabwe', value: 'ZW' },
];

export const checkPost = (postcode: string, country: string) => {
  if (!country || !postcode) {
    return true;
  }
  const checked = postalCodes.validate(country, postcode);
  return typeof checked === 'boolean' ? checked : false;
};

export const showSymbolOnboarding = (tick: string) => {
  switch (tick) {
    case 'YES':
      return '✔';
    case 'NO':
      return '✘';
    case '':
      return '';
    default:
      return 'N/A';
  }
};

export const isValidNINumber = (val: string) => {
  const niRegex =
    /^(?!BG|GB|NK|KN|TN|NT|ZZ|OO|CR)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z]\d{6}[A-D]?$/i;
  return niRegex.test(val);
};

// Mirrors backend validatePPSNumber (nxsys-backend src/utils/utils.ts)
export const isValidPPSNumber = (val: string) => {
  return /^[0-9]{7}[A-Z]{1,2}$/i.test(val);
};

export const formatPaymentType = (status: string) => {
  switch (status) {
    case 'FIXED':
      return 'Fixed';
    case 'RATE':
      return 'Rate';
    case 'VARIABLE':
      return 'Variable';
    case 'COST_OF_MATERIALS':
      return 'Cost of Material';
    case 'PAYMENT_ONLY_RATE':
      return 'Payment Only Rate';
    case 'PAYMENT_ONLY':
      return 'Payment Only';
    default:
      return status;
  }
};

export const formatPaymentUnit = (status: string) => {
  switch (status) {
    case 'DAILY':
      return 'Daily';
    case 'HOURLY':
      return 'Hourly';
    case 'WEEKLY':
      return 'Weekly';
    case 'MONTHLY':
      return 'Monthly';
    default:
      return status;
  }
};

export const makeCode = (firstKey = '', length = 6) => {
  let result = firstKey;
  const characters = '0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getVATCodeOption = (vatCode: VATCode) => ({
  label: `${vatCode.description} (${vatCode.VATCode}) - ${vatCode.VATRate}`,
  value: vatCode.id,
});

export const calculateHolidayBalanceBySubcontractor = (subcontractor: any) => {
  if (!subcontractor) return 0;
  const holidayBalance =
    Number(subcontractor?.setting?.holidayPayOpeningBalance || 0) +
    Number(subcontractor?.setting?.holidayPaySumOfAccrued || 0) -
    Number(subcontractor?.setting?.holidayPaySumOfPaid || 0);
  return holidayBalance;
};

export const isHolidayPayRate = (rateCode: string) =>
  Boolean(HP_CODES.includes(rateCode));

export const defaultPaymentTerm = [
  { label: 'On invoicing', value: 'ON_INVOICING' },
  { label: 'Full Cash Receipt', value: 'FULL_CASH_RECEIPT' },
  { label: 'Proportion to Cash receipt', value: 'PROPORTION_TO_CASH_RECEIPT' },
];

export const defaultInvoiceMethodOptions = [
  { label: 'One per timesheet', value: 'ONE_PER_TIMESHEET' },
  {
    label: 'Consolidate by Timesheet Batch',
    value: 'CONSOLIDATE_BY_TIMESHEET_BATCH',
  },
  { label: 'By Site', value: 'BY_SITE' },
];

export const defaultInvoiceRaisedAfterOptions = [
  { label: 'After confirmed payment', value: 'AFTER_CONFIRMED_PAYMENT' },
  { label: 'After draft payroll', value: 'AFTER_DRAFT_PAYROLL' },
];

export const defaultInvoiceRaisedMethodOptions = [
  { label: 'Raise difference only', value: 'RAISE_DIFFERENCE_ONLY' },
  { label: 'Raise all employment costs', value: 'RAISE_ALL_EMPLOYMENT_COSTS' },
];

export const defaultRuleInvoicingMethod = [
  { label: 'Of the following month', value: 'OF_THE_FOLLOWING_MONTH' },
  {
    label: 'Day(s) after the invoice date',
    value: 'DAYS_AFTER_THE_INVOICE_DATE',
  },
  { label: 'Of the current month', value: 'OF_THE_CURRENT_MONTH' },
];

export const defaultPayrollFreeMethod = [
  { label: 'Fixed Fee per employee', value: 'FIXED_FEE_PER_EMPLOYEE' },
  { label: 'Tiered fee', value: 'TIERED_FEE' },
];

export const defaultInvoiceTemplateOptions = [
  { label: 'Default template', value: 'DEFAULT' },
  { label: 'Breakdown per employee template', value: 'BREAKDOWN_PER_EMPLOYEE' },
];

export const defaultMarginMethod = [
  { label: 'Fixed Fee', value: 'FIXED_FEE' },
  { label: 'Fixed Fee Table', value: 'FIXED_FEE_TABLE' },
  { label: '% Table on Timesheet Value', value: '%_TABLE_ON_TIMESHEET_VALUE' },
  { label: '% On Timesheet Value', value: '%_ON_TIMESHEET_VALUE' },
  { label: 'None', value: 'NONE' },
];

export const PAYSLIP_TEMPLATES = {
  DEFAULT: { label: 'Nxsys Default', value: 'DEFAULT' },
  LAYOUT_1: { label: 'Layout 1', value: 'LAYOUT_1' },
  LAYOUT_2: { label: 'Layout 2', value: 'LAYOUT_2' },
  LAYOUT_3: { label: 'Layout 3', value: 'LAYOUT_3' },
  LAYOUT_4: { label: 'Layout 4', value: 'LAYOUT_4' },
  LAYOUT_5: { label: 'Layout 5', value: 'LAYOUT_PAYE_5' },
  LAYOUT_6: { label: 'Layout 6', value: 'LAYOUT_PAYE_6' },
  LAYOUT_7: { label: 'Layout 7', value: 'LAYOUT_PAYE_7' },
  CIS_LAYOUT_3: { label: 'Layout 3', value: 'CIS_LAYOUT_3' },
  CIS_LAYOUT_4: { label: 'Layout 4', value: 'CIS_LAYOUT_4' },
  PAYE_ETH_LAYOUT_2: { label: 'Layout 2', value: 'PAYE_ETH_LAYOUT_2' },
  PAYE_FTH_LAYOUT_3: { label: 'Layout 3', value: 'PAYE_FTH_LAYOUT_3' },
};

export const PAYMENT_MODEL_TEMPLATES = {
  PAYE: [
    PAYSLIP_TEMPLATES.DEFAULT,
    PAYSLIP_TEMPLATES.LAYOUT_1,
    PAYSLIP_TEMPLATES.LAYOUT_2,
    PAYSLIP_TEMPLATES.LAYOUT_3,
    PAYSLIP_TEMPLATES.LAYOUT_4,
    PAYSLIP_TEMPLATES.LAYOUT_5,
    PAYSLIP_TEMPLATES.LAYOUT_6,
  ],
  UMBRELLA: [
    PAYSLIP_TEMPLATES.DEFAULT,
    PAYSLIP_TEMPLATES.LAYOUT_2,
    PAYSLIP_TEMPLATES.LAYOUT_3,
  ],
  HYBRID: [PAYSLIP_TEMPLATES.DEFAULT, PAYSLIP_TEMPLATES.LAYOUT_2],
  PAYE_HYBRID: [
    PAYSLIP_TEMPLATES.DEFAULT,
    PAYSLIP_TEMPLATES.LAYOUT_1,
    PAYSLIP_TEMPLATES.LAYOUT_2,
    PAYSLIP_TEMPLATES.LAYOUT_3,
    PAYSLIP_TEMPLATES.LAYOUT_5,
  ],
  CIS: [
    PAYSLIP_TEMPLATES.DEFAULT,
    PAYSLIP_TEMPLATES.LAYOUT_1,
    PAYSLIP_TEMPLATES.LAYOUT_2,
    PAYSLIP_TEMPLATES.CIS_LAYOUT_3,
    PAYSLIP_TEMPLATES.CIS_LAYOUT_4,
  ],
  PAYE_FTH: [
    PAYSLIP_TEMPLATES.DEFAULT,
    PAYSLIP_TEMPLATES.PAYE_ETH_LAYOUT_2,
    PAYSLIP_TEMPLATES.PAYE_FTH_LAYOUT_3,
  ],
};

export const pensionCalCulationMethodOptions = [
  { label: 'Net Pay Arrangement', value: 'NET_PAY_ARRANGEMENT' },
  { label: 'Relief at Source', value: 'RELIEF_AT_SOURCE' },
];

export const getSalaryCalculationMethodOptions = (): Option<string>[] => {
  const salaryCalMethod = getAppConfig('salaryCalMethod') ?? {};
  return map(Object.keys(salaryCalMethod), (key: string) => ({
    label: salaryCalMethod[key as keyof typeof salaryCalMethod],
    value: key,
  }));
};

export const escapeCSV = (value: string) => {
  if (typeof value !== 'string') return value;
  let escaped = value.replace(/"/g, '""');
  if (/[",\n]/.test(escaped)) {
    escaped = `"${escaped}"`;
  }
  return escaped;
};

export const getYesNoNALabel = (value: string | undefined) => {
  switch (value) {
    case YesNoNAEnum.YES:
      return 'Yes';
    case YesNoNAEnum.NO:
      return 'No';
    case YesNoNAEnum.NOT_AVAILABLE:
      return 'N/A';
    default:
      return '-';
  }
};

export const calcUSCRateFrequency = (payFrequency: string, value: number) => {
  if (!value) return '0.00';
  switch (payFrequency) {
    case 'WEEKLY':
      return (value / 52)?.toFixed(2);
    case '2_WEEKLY':
      return (value / 52)?.toFixed(2);
    case '4_WEEKLY':
      return (value / 52)?.toFixed(2);
    case 'MONTHLY':
      return (value / 12)?.toFixed(2);
    default:
      return value;
  }
};

export const calcUSCRateAmount = (payFrequency: string, value: number) => {
  switch (payFrequency) {
    case 'WEEKLY':
      return (value * 52)?.toFixed(2);
    case '2_WEEKLY':
      return (value * 26)?.toFixed(2);
    case '4_WEEKLY':
      return (value * 13)?.toFixed(2);
    case 'MONTHLY':
      return (value * 12)?.toFixed(2);
    default:
      return value;
  }
};

export const formatMoneyNoDecimal = (number: number, currencySymbol = '£') => {
  if (number) {
    return `${currencySymbol} ${numeral(number).format('0,0')}`;
  }
  return 0;
};

export const getEstimatedCompletionTimeNest = (doneAt: string) => {
  const _doneAt = moment(doneAt);
  const now = moment();

  if (_doneAt.isAfter(now)) {
    return _doneAt.format('DD/MM/YYYY HH:mm');
  }

  return null;
};

export const calcGrossPay = (salary: number, payFrequency: string) => {
  switch (payFrequency) {
    case 'WEEKLY': {
      return salary / 52;
    }
    case '2_WEEKLY': {
      return salary / 26;
    }
    case '4_WEEKLY': {
      return salary / 13;
    }
    case 'MONTHLY':
      return salary / 12;
    default:
      return salary;
  }
};

export const getGreetingDashboard = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning,';
  if (hour < 17) return 'Good Afternoon,';
  return 'Good Evening,';
};

export const downloadFileByUrlWithCorrectFileName = async (
  fileUrl: string,
  fileName: string,
) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // const ext = fileName.substring(fileName.lastIndexOf('.'));
    // const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    // const date = moment().format('DD-MM-YYYY');
    // link.download = `${nameWithoutExt}_${date}${ext}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch {
    window.open(fileUrl, '_blank');
  }
};

export const getAge = (DOB: Date) => {
  if (!DOB) return undefined;
  const birthDate = DOB;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  birthDate.setUTCHours(0, 0, 0, 0);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const getArrayFromComaSeparateString = (stringVal: string) => {
  if (!stringVal) {
    return null;
  }
  return split(stringVal, ',').map((item) => Number(item.trim()));
};

export const convertArrayToString = (arrayVal: string[]) => {
  if (!arrayVal || !arrayVal?.length) {
    return '';
  }
  return arrayVal.map((item) => toString(item)).join(',');
};
